import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Flag, ExternalLink, Loader2, ShieldCheck } from "lucide-react";

const ChatReports = () => {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("open");
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/chat/reports${tab === "all" ? "" : `?status=${tab}`}`);
      setItems(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load reports");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const resolve = async (id) => {
    setBusy(id);
    try {
      await adminApi.post(`/chat/reports/${id}/resolve`, { note: "" });
      toast.success("Marked as reviewed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't resolve"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-8">
      <div className="flex items-center gap-3 mb-4">
        <Flag className="w-5 h-5 text-slate-700"/>
        <h1 className="font-display text-xl font-bold text-slate-900">Chat reports</h1>
      </div>

      <div className="mb-4 flex items-center gap-2" data-testid="chat-reports-tabs">
        {["open", "reviewed", "all"].map((k) => (
          <button
            key={k}
            data-testid={`chat-reports-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >{k}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500 text-sm">
          <ShieldCheck className="w-6 h-6 mx-auto text-emerald-500 mb-2"/>
          No {tab === "all" ? "" : tab} reports.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} data-testid={`chat-report-${r.id}`} className="rounded-2xl bg-white border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={r.status === "open" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}>
                      {r.status?.toUpperCase()}
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-700 uppercase text-[10px]">{r.reporter_role}</Badge>
                    <span className="text-[11px] text-slate-500">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="mt-2 text-sm font-medium text-slate-900" data-testid={`chat-report-reason-${r.id}`}>
                    {r.reason}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Parcel · <Link to={`/admin/parcels/${r.parcel_id}`} className="hover:underline text-slate-700 inline-flex items-center gap-1">
                      {r.parcel_id?.slice(0, 8)} <ExternalLink className="w-3 h-3"/>
                    </Link>
                    {" · "}
                    {r.parcel?.pickup_location || "?"} → {r.parcel?.drop_location || "?"}
                  </div>
                  {r.message_snapshot && (
                    <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700 whitespace-pre-wrap">
                      "{r.message_snapshot}"
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-2">
                    Reporter · <span className="text-slate-800">{r.reporter?.name || "—"}</span>
                    {r.reporter?.phone && <span className="ml-1 text-slate-400">({r.reporter.phone})</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/admin/parcels/${r.parcel_id}`}>
                    <Button variant="outline" size="sm" className="rounded-full">Open conversation</Button>
                  </Link>
                  {r.status === "open" && (
                    <Button
                      size="sm"
                      onClick={() => resolve(r.id)}
                      disabled={busy === r.id}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`chat-report-resolve-${r.id}`}
                    >
                      {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Mark reviewed"}
                    </Button>
                  )}
                </div>
              </div>
              {r.status === "reviewed" && r.resolved_at && (
                <div className="mt-2 text-[11px] text-slate-500">
                  Reviewed {new Date(r.resolved_at).toLocaleString()}
                  {r.resolution_note && ` · ${r.resolution_note}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatReports;
